/**
 * Shared setup gate for the data pages. The backend hides all metrics until
 * onboarding is complete; these helpers turn that into the right screen:
 *   locked  -> "Finish setup to see your numbers"
 *   pending -> "Pulling your numbers…" (first sync running)
 *   ready   -> show the real page
 */

import { useQuery, useMutation, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useApiClient } from "./api-context.js";

export interface SetupStatus {
  completed: boolean;
  previewAllowed: boolean;
  locked: boolean;
  requiredComplete: boolean;
  syncStatus: "pending" | "ready" | "error";
  lastSyncedAt: string | null;
  errorMessage: string | null;
}

export function useSetupGate(): UseQueryResult<SetupStatus, Error> {
  const api = useApiClient();
  return useQuery<SetupStatus, Error>({
    queryKey: ["dashboard", "status"],
    queryFn: () => api.get<SetupStatus>("/dashboard/status"),
    // Poll while not done or still pulling; stop once data is ready.
    refetchInterval: (q) => {
      const d = q.state.data;
      if (!d) return 15_000;
      if (!d.previewAllowed) return 15_000;
      if (d.syncStatus === "pending") return 15_000;
      return false;
    },
  });
}

export function SetupLocked(): React.ReactElement {
  return (
    <div style={panel.wrap}>
      <div style={panel.icon}>🔒</div>
      <h2 style={panel.title}>Finish setup to see your numbers</h2>
      <p style={panel.body}>
        You're almost there. Finish the setup steps and your dashboard fills in.
      </p>
      <Link to="/welcome" style={panel.button}>
        Finish setup →
      </Link>
    </div>
  );
}

/**
 * Shown when the order pull failed — instead of pretending it's still
 * "Pulling…" forever. Plain copy + a one-click retry.
 */
export function SyncError({ message }: { message?: string | null }): React.ReactElement {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const retry = useMutation({
    mutationFn: () => api.post("/sync-jobs/ultracart/sync-now"),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["dashboard", "status"] });
      void queryClient.invalidateQueries({ queryKey: ["sync-jobs", "active"] });
    },
  });
  return (
    <div style={panel.wrap}>
      <div style={panel.icon}>⚠️</div>
      <h2 style={panel.title}>We couldn't pull your orders</h2>
      <p style={panel.body}>
        {message || "Something went wrong. Try again — if it keeps happening, reach out and we'll help."}
      </p>
      <button style={panel.button} onClick={() => retry.mutate()} disabled={retry.isPending}>
        {retry.isPending ? "Trying again…" : "Try again"}
      </button>
    </div>
  );
}

interface SyncJob {
  id: string;
  jobType: string;
  status: "queued" | "running";
  progressCurrent: number | null;
  progressTotal: number | null;
  queuedAt: string;
  startedAt: string | null;
}

/**
 * Live "we're working" screen. Polls /sync-jobs/active every few seconds and
 * shows a real progress bar + running count, so the user can SEE it moving
 * instead of staring at a static "come back later".
 */
export function PullingData(): React.ReactElement {
  const api = useApiClient();
  const { data } = useQuery({
    queryKey: ["sync-jobs", "active"],
    queryFn: () => api.get<{ jobs: SyncJob[] }>("/sync-jobs/active"),
    refetchInterval: 3_000,
  });

  const jobs = data?.jobs ?? [];
  // Prefer an order pull (the long one); fall back to whatever's running.
  const job = jobs.find((j) => j.jobType.includes("pull")) ?? jobs[0];
  const current = job?.progressCurrent ?? 0;
  const total = job?.progressTotal ?? null;
  const pct = total && total > 0 ? Math.min(100, Math.round((current / total) * 100)) : null;

  let countLine: string;
  if (pct !== null) countLine = `${current.toLocaleString()} of ${total!.toLocaleString()} orders (${pct}%)`;
  else if (current > 0) countLine = `${current.toLocaleString()} orders pulled so far…`;
  else if (job) countLine = "Starting the pull…";
  else countLine = "Getting things going…";

  return (
    <div style={panel.wrap}>
      <div style={panel.spinnerRow}>
        <div className="dd-spinner" style={panel.spinner} />
      </div>
      <h2 style={panel.title}>Pulling your numbers…</h2>

      <div style={panel.track}>
        {pct !== null ? (
          <div style={{ ...panel.fill, width: `${pct}%` }} />
        ) : (
          <div className="dd-indeterminate" style={panel.fillIndeterminate} />
        )}
      </div>
      <p style={panel.count}>{countLine}</p>

      <p style={panel.body}>
        This can take a few minutes. You can leave and come back — it keeps
        running on its own.
      </p>
    </div>
  );
}

const panel: Record<string, React.CSSProperties> = {
  wrap: {
    maxWidth: 460,
    margin: "64px auto",
    textAlign: "center",
    background: "#161B27",
    border: "1px solid #1E2230",
    borderRadius: 12,
    padding: "40px 32px",
  },
  icon: { fontSize: 36, marginBottom: 12 },
  title: { margin: 0, fontSize: 20 },
  body: { color: "#7C829A", fontSize: 14, marginTop: 16, lineHeight: 1.5 },
  spinnerRow: { display: "flex", justifyContent: "center", marginBottom: 16 },
  spinner: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: "3px solid #1E2230",
    borderTopColor: "#4ADE80",
  },
  track: {
    position: "relative",
    height: 8,
    background: "#0D0F14",
    borderRadius: 4,
    overflow: "hidden",
    marginTop: 20,
  },
  fill: { height: "100%", background: "#4ADE80", borderRadius: 4, transition: "width 0.4s ease" },
  fillIndeterminate: {
    position: "absolute",
    top: 0,
    bottom: 0,
    background: "#4ADE80",
    borderRadius: 4,
  },
  count: { color: "#E6E6E6", fontSize: 14, marginTop: 12, fontVariantNumeric: "tabular-nums" },
  button: {
    display: "inline-block",
    marginTop: 20,
    background: "#4ADE80",
    color: "#0D0F14",
    padding: "10px 20px",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    textDecoration: "none",
    border: "none",
    cursor: "pointer",
  },
};
